/**
 * Workforce analytics (company portal, WP3A). Mirrors the backend
 * `company-analytics` module: `GET companies/:companyId/analytics/workforce`
 * and `.../workforce/departures`.
 */

/** Reporting period presets, resolved on the company calendar. */
export enum AnalyticsPeriod {
  Last12Months = 'last_12_months',
  LastQuarter = 'last_quarter',
  YearToDate = 'year_to_date',
  LastYear = 'last_year',
}

/** How the "Where to look closer" table groups the scope. */
export enum AnalyticsGroupBy {
  Location = 'location',
  Department = 'department',
  Manager = 'manager',
}

export enum TenureBand {
  Under6Months = 'under_6_months',
  Months6To12 = 'months_6_to_12',
  Years1To2 = 'years_1_to_2',
  Years2To5 = 'years_2_to_5',
  Years5Plus = 'years_5_plus',
}

export type DepartureType = 'voluntary' | 'involuntary';

/**
 * Why an employee left (termination `reasonCategory`), mirror of the backend
 * `EmployeeTerminationReasonCategory`.
 */
export enum TerminationReasonCategory {
  CareerOpportunity = 'career_opportunity',
  Compensation = 'compensation',
  Relocation = 'relocation',
  Personal = 'personal',
  Retirement = 'retirement',
  WorkEnvironment = 'work_environment',
  Performance = 'performance',
  Conduct = 'conduct',
  Restructuring = 'restructuring',
  /** Mise à pied: shortage of work (ROE code A). */
  Layoff = 'layoff',
  EndOfContract = 'end_of_contract',
  Other = 'other',
}

/** Departures with no reason category on record. */
export const UNKNOWN_DEPARTURE_REASON = 'unknown';

/** Filter value / group key for "no department" and "no manager". */
export const ANALYTICS_NONE_KEY = 'none';

/** The categories that fit each departure type ("other" fits both). */
export const REASON_CATEGORIES_BY_TYPE: Record<
  DepartureType,
  TerminationReasonCategory[]
> = {
  voluntary: [
    TerminationReasonCategory.CareerOpportunity,
    TerminationReasonCategory.Compensation,
    TerminationReasonCategory.Relocation,
    TerminationReasonCategory.Personal,
    TerminationReasonCategory.Retirement,
    TerminationReasonCategory.WorkEnvironment,
    TerminationReasonCategory.Other,
  ],
  involuntary: [
    TerminationReasonCategory.Performance,
    TerminationReasonCategory.Conduct,
    TerminationReasonCategory.Restructuring,
    TerminationReasonCategory.Layoff,
    TerminationReasonCategory.EndOfContract,
    TerminationReasonCategory.Other,
  ],
};

export interface AnalyticsDateRange {
  /** `YYYY-MM-DD`, inclusive */
  from: string;
  to: string;
}

export interface WorkforceMetrics {
  openingHeadcount: number;
  closingHeadcount: number;
  averageHeadcount: number;
  hires: number;
  departures: number;
  voluntary: number;
  involuntary: number;
  unclassified: number;
  netChange: number;
  /** Departures / average daily headcount x 100, not annualized. */
  turnoverRate: number | null;
  voluntaryRate: number | null;
  involuntaryRate: number | null;
  firstYearDepartures: number;
  firstYearShare: number | null;
  averageTenureYears: number | null;
  medianTenureYears: number | null;
  averageTenureAtDepartureYears: number | null;
  underOneYear: number | null;
  /** Average headcount under 10: rates, tenure and person rows hidden. */
  withheld: boolean;
}

export interface WorkforceMonth {
  /** `YYYY-MM` */
  month: string;
  from: string;
  to: string;
  averageHeadcount: number;
  closingHeadcount: number;
  hires: number;
  departures: number;
  voluntary: number;
  involuntary: number;
  unclassified: number;
  turnoverRate: number | null;
  voluntaryRate: number | null;
  involuntaryRate: number | null;
  averageTenureYears: number | null;
}

export interface DepartureReasonCount {
  /** A `TerminationReasonCategory` or `unknown`. */
  reason: string;
  voluntary: number;
  involuntary: number;
  unclassified: number;
  total: number;
}

export interface TenureBandCount {
  band: TenureBand;
  count: number;
}

export interface WorkforceGroupRow {
  key: string;
  /** Null for the "no department" / "no manager" group. */
  label: string | null;
  departmentCount: number;
  managerCount: number;
  current: WorkforceMetrics;
  previous: WorkforceMetrics | null;
}

export interface WorkforceDataCoverage {
  missingStartDate: number;
  inactiveWithoutDepartureDate: number;
  departureBeforeStart: number;
  unclassifiedDepartures: number;
  missingReason: number;
  noDepartment: number;
  noManager: number;
  priorSpellDepartures: number;
}

export interface AnalyticsScopeOption {
  id: string;
  name: string;
}

export interface WorkforceAnalytics {
  period: {
    preset: AnalyticsPeriod;
    current: AnalyticsDateRange;
    previous: AnalyticsDateRange;
  };
  scope: {
    branchId: string | null;
    departmentId: string | null;
    managerId: string | null;
    wholeCompany: boolean;
  };
  /**
   * The viewer may read the employee records of the scope (employee.read):
   * nothing is withheld for them.
   */
  fullAccess: boolean;
  current: WorkforceMetrics;
  previous: WorkforceMetrics;
  months: WorkforceMonth[];
  reasons: DepartureReasonCount[];
  tenureBands: TenureBandCount[];
  groupBy: AnalyticsGroupBy;
  groups: WorkforceGroupRow[];
  coverage: WorkforceDataCoverage;
  options: {
    branches: AnalyticsScopeOption[];
    departments: AnalyticsScopeOption[];
    managers: AnalyticsScopeOption[];
  };
}

export interface WorkforceDeparture {
  employeeId: string;
  name: string;
  referenceId: string | null;
  branchName: string;
  departmentName: string | null;
  managerName: string | null;
  date: string;
  type: DepartureType | null;
  reason: string;
  tenureYears: number | null;
  firstYear: boolean;
}

export interface WorkforceDepartures {
  range: AnalyticsDateRange;
  withheld: boolean;
  averageHeadcount: number;
  turnoverRate: number | null;
  averageTenureAtDepartureYears: number | null;
  departures: WorkforceDeparture[];
}

/** Query of both endpoints (`month` only on departures). */
export interface WorkforceAnalyticsParams {
  period: AnalyticsPeriod;
  branchId?: string;
  departmentId?: string;
  managerId?: string;
  groupBy?: AnalyticsGroupBy;
  month?: string;
}

// ── Part B (WP3B): Leave & attendance, Retention, Demographics ─────────────

/**
 * Leave buckets. Each company leave type lands in one by its name (backend
 * `utils/leave-buckets.util.ts`); `LeaveAnalytics.leaveTypes` lists them.
 */
export enum LeaveBucket {
  Vacation = 'vacation',
  Sickness = 'sickness',
  Personal = 'personal',
  Other = 'other',
}

export const LEAVE_BUCKETS: LeaveBucket[] = [
  LeaveBucket.Vacation,
  LeaveBucket.Sickness,
  LeaveBucket.Personal,
  LeaveBucket.Other,
];

export type LeaveBucketTotals = Record<LeaveBucket, number>;

export enum LeaveRecordKind {
  Requests = 'requests',
  NoVacation = 'no_vacation',
  Balances = 'balances',
}

export interface LeaveMetrics {
  averageHeadcount: number;
  requests: number;
  totalDays: number;
  totalHours: number;
  /** Null when withheld (a small group's split by bucket is personal). */
  days: LeaveBucketTotals | null;
  hours: LeaveBucketTotals | null;
  scheduledHours: number;
  /** Sickness hours / scheduled hours x 100. */
  absenceRate: number | null;
  daysPerEmployee: number | null;
  withheld: boolean;
}

export interface LeaveMonth {
  month: string;
  from: string;
  to: string;
  totalDays: number;
  totalHours: number;
  days: LeaveBucketTotals | null;
  hours: LeaveBucketTotals | null;
}

/** Today's vacation balances (the vacation wallets of active employees). */
export interface VacationUsage {
  asOf: string;
  activeEmployees: number;
  employeesWithWallet: number;
  entitlementDays: number;
  takenDays: number;
  remainingDays: number;
  entitlementHours: number;
  takenHours: number;
  remainingHours: number;
  utilization: number | null;
  noVacation: number;
  noVacationEligible: number;
}

export interface LeaveGroupRow {
  key: string;
  label: string | null;
  departmentCount: number;
  managerCount: number;
  activeCount: number;
  current: LeaveMetrics;
  previous: LeaveMetrics | null;
  vacation: VacationUsage;
}

export interface LeaveTypeUsage {
  id: string;
  name: string;
  bucket: LeaveBucket;
  days: number | null;
}

export interface LeaveCoverage {
  otherTypes: number;
  requestsOnDaysOff: number;
  activeWithoutStartDate: number;
  activeWithoutVacationWallet: number;
}

export interface LeaveAnalytics {
  period: { current: AnalyticsDateRange; previous: AnalyticsDateRange };
  scope: WorkforceAnalytics['scope'];
  /**
   * The viewer may read the employee records of the scope (employee.read):
   * nothing is withheld for them.
   */
  fullAccess: boolean;
  current: LeaveMetrics;
  previous: LeaveMetrics;
  months: LeaveMonth[];
  vacation: VacationUsage;
  leaveTypes: LeaveTypeUsage[];
  groups: LeaveGroupRow[];
  coverage: LeaveCoverage;
}

export interface LeaveRecordRow {
  requestId: string;
  employeeId: string;
  name: string;
  branchName: string;
  departmentName: string | null;
  leaveTypeName: string;
  bucket: LeaveBucket;
  startDate: string;
  endDate: string;
  days: number;
  hours: number;
}

export interface NoVacationRow {
  employeeId: string;
  name: string;
  branchName: string;
  departmentName: string | null;
  serviceStart: string;
  lastVacation: string | null;
  remainingDays: number | null;
}

export interface VacationBalanceRow {
  employeeId: string;
  name: string;
  branchName: string;
  departmentName: string | null;
  entitlementDays: number;
  takenDays: number;
  remainingDays: number;
  entitlementHours: number;
  takenHours: number;
  remainingHours: number;
  utilization: number | null;
}

export interface LeaveRecords {
  range: AnalyticsDateRange;
  withheld: boolean;
  requests: LeaveRecordRow[];
  noVacation: NoVacationRow[];
  balances: VacationBalanceRow[];
}

export interface LeaveRecordsParams extends WorkforceAnalyticsParams {
  kind?: LeaveRecordKind;
}

export enum RetentionMilestone {
  Days90 = 'd90',
  FirstYear = 'd365',
}

/**
 * Rates and outcomes need at least this many eligible hires (for a viewer
 * without employee.read).
 */
export const RETENTION_MIN_ELIGIBLE = 5;

export interface MilestoneRate {
  eligible: number;
  retained: number | null;
  rate: number | null;
  withheld: boolean;
}

export interface RetentionMetrics {
  d90: MilestoneRate;
  d365: MilestoneRate;
  hires: number;
}

export interface RetentionCohort {
  /** `YYYY-Qn` */
  quarter: string;
  d90: MilestoneRate;
  d365: MilestoneRate;
}

export interface RetentionGroupRow {
  key: string;
  label: string | null;
  departmentCount: number;
  managerCount: number;
  activeCount: number;
  current: RetentionMetrics;
  previous: RetentionMetrics | null;
}

export interface RetentionAnalytics {
  period: { current: AnalyticsDateRange; previous: AnalyticsDateRange };
  /**
   * The viewer may read the employee records of the scope (employee.read):
   * nothing is withheld for them.
   */
  fullAccess: boolean;
  current: RetentionMetrics;
  previous: RetentionMetrics;
  cohorts: RetentionCohort[];
  awaiting: { d90: number; d365: number };
  groups: RetentionGroupRow[];
  coverage: { missingStartDate: number; priorSpells: number };
}

export interface RetentionOutcome {
  employeeId: string;
  name: string;
  branchName: string;
  departmentName: string | null;
  managerName: string | null;
  startDate: string;
  milestoneDate: string;
  retained: boolean;
  leftOn: string | null;
}

export interface RetentionHires {
  range: AnalyticsDateRange;
  milestone: RetentionMilestone;
  eligible: number;
  withheld: boolean;
  outcomes: RetentionOutcome[];
}

export interface RetentionHiresParams extends WorkforceAnalyticsParams {
  milestone?: RetentionMilestone;
  quarter?: string;
}

export type DemographicsAgeBand =
  'under_25' | '25_34' | '35_44' | '45_54' | '55_plus' | 'unknown';

export type DemographicsGender =
  'female' | 'male' | 'other' | 'prefer_not_to_say' | 'unknown';

export const DEMOGRAPHICS_AGE_BANDS: DemographicsAgeBand[] = [
  'under_25',
  '25_34',
  '35_44',
  '45_54',
  '55_plus',
  'unknown',
];

export const DEMOGRAPHICS_GENDERS: DemographicsGender[] = [
  'female',
  'male',
  'other',
  'prefer_not_to_say',
  'unknown',
];

/**
 * Demographics need this many active people in a scope or group (for a
 * viewer without employee.read).
 */
export const DEMOGRAPHICS_MIN_HEADCOUNT = 10;
/** Counts below this are hidden (for a viewer without employee.read). */
export const DEMOGRAPHICS_MIN_CELL = 5;

/** A count, or null when hidden by the small-count rule. */
export type ProtectedCount = number | null;

export interface DemographicsBreakdown {
  activeEmployees: number;
  withheld: boolean;
  knownAgeCount: number | null;
  averageAge: number | null;
  ageBands: {
    key: DemographicsAgeBand;
    count: ProtectedCount;
    genders: Record<DemographicsGender, ProtectedCount>;
  }[];
  genders: { key: DemographicsGender; count: ProtectedCount }[];
}

export interface DemographicsGroupRow {
  key: string;
  label: string | null;
  departmentCount: number;
  managerCount: number;
  activeCount: number;
  breakdown: DemographicsBreakdown;
}

export interface DemographicsAnalytics {
  period: { current: AnalyticsDateRange; previous: AnalyticsDateRange };
  /**
   * The viewer may read the employee records of the scope (employee.read):
   * nothing is withheld for them.
   */
  fullAccess: boolean;
  asOf: string;
  current: DemographicsBreakdown;
  previous: { activeEmployees: number; averageAge: number | null };
  departments: { id: string | null; name: string | null; count: number }[];
  groups: DemographicsGroupRow[];
}
